"use client";

import { useEffect, useState } from "react";
import { updateClarityConsent } from "@/components/analytics/clarity_consent";
import { MetaPixel } from "@/components/analytics/meta_pixel";
import { updateGoogleConsent } from "@/lib/google-ads-events";
import { SCROLL_LOCK_FULL_WIDTH_CLASS } from "@/lib/scroll-lock";
import { cn } from "@/lib/utils";

const CONSENT_KEY = "cookie_consent";

type ConsentState = "undecided" | "accepted" | "declined";

function getStoredConsent(): ConsentState {
	if (typeof window === "undefined") return "undecided";
	const stored = localStorage.getItem(CONSENT_KEY);
	if (stored === "accepted" || stored === "declined") return stored;
	return "undecided";
}

export function CookieConsentWrapper() {
	const [consent, setConsent] = useState<ConsentState>("undecided");
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
		setConsent(getStoredConsent());
	}, []);

	const handleAccept = () => {
		localStorage.setItem(CONSENT_KEY, "accepted");
		updateGoogleConsent(true);
		updateClarityConsent(true);
		setConsent("accepted");
	};

	const handleDecline = () => {
		localStorage.setItem(CONSENT_KEY, "declined");
		updateGoogleConsent(false);
		updateClarityConsent(false);
		setConsent("declined");
	};

	if (!mounted) return null;

	return (
		<>
			{consent === "accepted" && <MetaPixel />}

			{consent === "undecided" && (
				<div
					data-highrays-cookie-consent=""
					className={cn(
						"fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] z-[90] sm:inset-x-5 sm:bottom-5",
						SCROLL_LOCK_FULL_WIDTH_CLASS,
					)}
				>
					<div className="mx-auto flex max-w-xl items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 p-2 text-white shadow-2xl sm:gap-3 sm:rounded-2xl sm:p-4">
						<p className="flex-1 text-xs leading-relaxed text-zinc-300 sm:text-sm">
							<span className="sm:hidden" aria-hidden="true">
								Analytics?
							</span>
							<span className="sr-only sm:not-sr-only">
								Allow analytics to help us improve the site and checkout.
								Declining keeps only essential settings.
							</span>
						</p>
						<div className="flex shrink-0 gap-2">
							<button
								type="button"
								onClick={handleDecline}
								className="min-h-11 rounded-full border border-zinc-700 px-3 text-xs font-medium text-zinc-300 transition-colors hover:text-white sm:px-4 sm:text-sm"
							>
								Decline
							</button>
							<button
								type="button"
								onClick={handleAccept}
								aria-label="Allow analytics"
								className="min-h-11 rounded-full bg-red-600 px-3 text-xs font-bold text-white transition-colors hover:bg-red-700 sm:px-4 sm:text-sm"
							>
								<span className="sm:hidden">Allow</span>
								<span className="hidden sm:inline">Allow analytics</span>
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
